/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, ScrollView } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Photo from '@components/global/Photo';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import BranchSearch from '@components/form/search/BranchSearch';
import * as Layout from '@components/screen/Layout';
import { isWeb, isNative, isAndroid } from '@global/screens';
import { showPopup } from '@/store/modal';
import ScrollContainer from '@components/global/ScrollContainer';

import * as Resources from '@/store/resources/actions';
import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';
import { MsOrder } from '@api/ms-order';
import { uploadS3, chooseImagePicker } from '@global/helpers';
import { SALESMAN_TYPE } from '@/constants';

const photoSize = 120;

const UserFormScreen = (props) => {
  const userRef = useRef();

  const dispatch = useDispatch();
  const countryList = [
    {
      name: 'Indonesia',
      value: 'Indonesia',
    },
    {
      name: 'Vietnam',
      value: 'Vietnam',
    },
  ];
  const roleLists = useSelector((state) => state.resources.roles);

  const [detailData, setDetailData] = useState({});
  const [salesTeamTemp, setSalesTeamTemp] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState({
    branch: false,
  });
  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);

  const getUserDetail = useApi(MsAccount.getUserDetail);
  const getBranches = useApi(MsAccount.getBranches);
  const getLpbCarrier = useApi(MsOrder.getLpbCarrier);

  const requiredField = (role, roles) => {
    const newRole = role ? (role.code ? role.code : role) : '';
    if (roles.indexOf(newRole) > -1) {
      return false;
    }
    return true;
  };

  const disabled_some_fields = [
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'general_manager_marketing',
    'marketing_support',
    'marketing_admin',
    'billing_supervisor',
    'chief_of_staff',
    '3pl',
    'general',
    'national_key_account_manager',
    'promotor',
  ];
  const lead_roles = [
    'vice_president',
    'distributor_manager',
    'general_manager',
    'superadmin',
    'general',
    ...disabled_some_fields,
  ];
  const sales_code_roles = [
    'salesman',
    'key_account_manager',
    'group_key_account_manager',
  ];
  const salesman_type_roles = ['salesman'];

  const FormValidationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Name is required.')
      .test(
        'alphabets',
        'Name must contain alphabets',
        (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
      ),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required.'),
    phone: Yup.number()
      .typeError('Phone number must be a number')
      .max(1234567890123, 'Max 13 characters.')
      .required('Phone number is required.'),
    country: Yup.array().required('Country is required.'),
    role: Yup.object().required('Role is required.'),
    branch: Yup.array()
      .required('Branch is required.')
      .when('role', (role) => {
        const newRole = role ? (role.code ? role.code : role) : '';
        if (lead_roles.indexOf(newRole) > -1) {
          return Yup.array();
        }
        return Yup.array()
          .required('Branch is required.')
          .test(
            'branch-required',
            'Branch is required.',
            (val) => val && val.length && lead_roles.indexOf(newRole) == -1,
          );
      }),
    sales_team: Yup.array()
      .required('Sales team is required.')
      .when('role', (role) => {
        const newRole = role ? (role.code ? role.code : role) : '';
        if (lead_roles.indexOf(newRole) > -1) {
          return Yup.array();
        }
        return Yup.array()
          .required('Sales team is required.')
          .test(
            'salesteam-required',
            'Sales team is required.',
            (val) => val && val.length && lead_roles.indexOf(newRole) == -1,
          );
      }),
    sales_code: Yup.string()
      .required('Sales code is required.')
      .when('role', (role) => {
        const newRole = role ? (role.code ? role.code : role) : '';
        if (sales_code_roles.indexOf(newRole) == -1) {
          return Yup.string();
        }
        return Yup.string().required('Sales code is required.');
      }),
    salesman_type: Yup.string().when('role', (role) => {
      const newRole = role ? (role.code ? role.code : role) : '';
      if (salesman_type_roles.indexOf(newRole) == -1) {
        return Yup.string();
      }
      return Yup.string().required('Salesman type is required.');
    }),
    carrier: Yup.object()
      .required('Carrier is required.')
      .when('role', (role) => {
        const newRole = role ? (role.code ? role.code : role) : '';
        if (newRole != '3pl') {
          return Yup.object();
        }
        return Yup.object().required('Carrier is required.');
      }),
  });

  const getDetail = async (id) => {
    try {
      const response = await getUserDetail.request(id);
      if (response?.data?.data && Object.keys(response.data.data).length) {
        const result = response.data.data;
        setDetailData(result);
        setTimeout(() => {
          setIsReady(true);
        }, 500);
      } else {
        setDetailData({});
        setTimeout(() => {
          setIsReady(true);
        }, 500);
      }
    } catch (error) {}
  };

  const searchBranchOriginal = async (ids = []) => {
    if (ids?.length) {
      try {
        const filter = {
          _id: { $in: ids },
        };
        const response = await getBranches.request({
          params: {
            filter: filter,
            skip: 0,
            limit: ids.length,
          },
        });
        if (response.status == 200 && response?.data?.data?.length) {
          const result = response.data.data.map((item) => {
            return {
              id: item._id,
              name: item.name,
              sales_team: item.sales_team,
            };
          });
          if (userRef.current) {
            userRef.current.setFieldValue('branch', result);

            if (result?.length) {
              const teams = [];
              for (let i = 0; i < result.length; i++) {
                const child = result[i];
                if (child && child.sales_team && child.sales_team.length) {
                  for (let i = 0; i < child.sales_team.length; i++) {
                    const tm = child.sales_team[i];
                    if (
                      tm &&
                      tm.id &&
                      teams.findIndex((a) => a.id == tm.id) == -1
                    ) {
                      teams.push(tm);
                    }
                  }
                }
              }

              setSalesTeamTemp(teams);

              if (
                !requiredField(userRef.current.role, [
                  'branch_head',
                  'finance_supervisor',
                  'regional_manager',
                  'regional_support',
                  'key_account_manager',
                  'group_key_account_manager',
                  'national_key_account_manager',
                  'salesman',
                  'sales_supervisor',
                  'sales_admin',
                  'regional_distributor_manager',
                ])
              ) {
                if (
                  !requiredField(userRef.current.role, [
                    'branch_head',
                    'regional_manager',
                    'regional_support',
                    'key_account_manager',
                    'group_key_account_manager',
                    'national_key_account_manager',
                    'regional_distributor_manager',
                  ])
                ) {
                  userRef.current.setFieldValue('sales_team', teams);
                } else if (
                  !requiredField(userRef.current.role, [
                    'branch_head',
                    'salesman',
                    'sales_supervisor',
                    'finance_supervisor',
                    'sales_admin',
                  ])
                ) {
                  userRef.current.setFieldValue('sales_team', []);
                } else {
                  userRef.current.setFieldValue('sales_team', []);
                }
              }
            }
          }
        }
      } catch (error) {}
    }
  };

  const getCarriers = async ({ val = null, skip = 0 }) => {
    setCarriersLoading(true);

    const response = await getLpbCarrier.request();
    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(carriers));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }

      if (val) {
        // Manual search
        finalResult = finalResult.filter((c) =>
          c.name.toLowerCase().includes(val.toLowerCase()),
        );
      }

      setCarriers(finalResult);
    }
    setCarriersLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsReady(false);
      setIsEdit(false);
      setDetailData({});
      setSalesTeamTemp([]);
      dispatch(Resources.getRoles({}));
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      } else {
        setTimeout(() => {
          setIsReady(true);
        }, 800);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    let mounted = true;
    if (mounted && isReady && isEdit && detailData?.branch?.length) {
      searchBranchOriginal(
        detailData.branch.filter((a) => a.id).map((a) => a.id),
      );
    }
    return () => {
      mounted = false;
    };
  }, [detailData, isReady]);

  const chooseFile = async () => {
    const image = await chooseImagePicker(1000, false);
    if (image && image.uri) {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      try {
        const url = await uploadS3({ file: image, isImage: true });
        dispatch(showPopup({ name: 'isLoading', value: false }));
        if (url) {
          return url;
        }
        Toast.show({
          text1: 'Failed to upload file. Please try again.',
          type: 'error',
        });
      } catch (error) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
      }
    }
    return false;
  };

  let routeParams = {};
  let branchName = '';
  let titleScreen = '';
  if (
    props &&
    props.route &&
    props.route.params &&
    Object.keys(props.route.params).length
  ) {
    if (props.route.params.branchName) {
      branchName = props.route.params.branchName;
      routeParams = {
        screen: 'BranchListNavigator',
        params: {
          screen: 'BranchListDetailScreen',
          params: props.route.params,
        },
      };
    } else if (props.route.params.id) {
      titleScreen = props.route.params.titleScreen;
      routeParams = {
        screen: 'ProfileNavigator',
        params: {
          screen: 'ProfileScreen',
          params: props.route.params,
        },
      };
    }
    if (props.route.params.fromTable) {
      routeParams = {};
      titleScreen = '';
    }
  }

  const MobileView = isWeb ? ScrollView : ScrollContainer;

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Back
          title={branchName ? branchName : titleScreen || 'Users'}
          onCallback={() => {
            routeParams && Object.keys(routeParams).length
              ? props.navigation.navigate(
                  routeParams.screen,
                  routeParams.params,
                )
              : props.navigation.navigate('UserNavigator', {
                  screen: 'UsersScreen',
                  params: { id: '', name: '', titleScreen: '' },
                });
          }}
        />
      ) : (
        false
      )}
      {!isReady ? (
        <Layout.Wrapper rounded>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (
        <Formik
          innerRef={userRef}
          initialValues={{
            image: isEdit && detailData?.image ? detailData.image : '',
            name: isEdit && detailData?.name ? detailData.name : '',
            email: isEdit && detailData?.email ? detailData.email : '',
            phone: isEdit && detailData?.phone ? detailData.phone : '',
            country:
              isEdit && detailData?.country && detailData?.country?.length
                ? detailData.country
                    .map((item) =>
                      countryList.find((country) => country.value === item),
                    )
                    .filter(Boolean)
                : [],
            role: isEdit && detailData?.role?.code ? detailData.role : '',
            branch:
              isEdit && detailData?.branch?.length ? detailData.branch : [],
            sales_team:
              isEdit &&
              Array.isArray(detailData?.sales_team) &&
              detailData.sales_team.length
                ? detailData.sales_team
                : [],
            sales_code:
              isEdit && detailData?.sales_code ? detailData.sales_code : '',
            salesman_type:
              isEdit && detailData?.salesman_type
                ? detailData.salesman_type
                : '',
            carrier: isEdit && detailData?.carrier ? detailData.carrier : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              payload.role =
                payload.role && payload.role.code ? payload.role : '';
              payload.country =
                payload.country && payload.country.length
                  ? payload.country.filter(Boolean).map((item) => item.value)
                  : [];
              payload.branch =
                payload.branch && payload.branch.length
                  ? payload.branch.map((item) => {
                      return { id: item.id, name: item.name };
                    })
                  : [];
              payload.sales_team =
                payload.sales_team && payload.sales_team.length
                  ? payload.sales_team.map((item) => {
                      return { id: item.id, name: item.name };
                    })
                  : [];

              if (!payload.salesman_type) {
                delete payload.salesman_type; //payload.salesman_type can be "" too
              }
              if (payload.country && payload.country.length) {
                payload.is_active_for = payload.country.map((country) =>
                  country === 'Indonesia' ? 'b2b' : 'b2b_vn',
                );
              }
              if (isEdit) {
                // delete payload.role
                // delete payload.branch
                // delete payload.sales_team
                // delete payload.sales_code
                const updateData = await MsAccount.updateUser(
                  detailData._id,
                  payload,
                );
                if (updateData && updateData.data && updateData.data.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  if (isEdit) {
                    props.navigation.navigate('ProfileNavigator', {
                      screen: 'ProfileScreen',
                      params: {
                        titleScreen: detailData.name,
                        name: detailData.name,
                        id: detailData._id,
                      },
                    });
                  } else {
                    props.navigation.navigate('UsersScreen', {
                      titleScreen: detailData.name,
                      name: detailData.name,
                      id: detailData._id,
                    });
                  }
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              } else {
                const createData = await MsAccount.createUser(payload);
                if (createData && createData.data && createData.data.data) {
                  const result = createData.data.data;
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'User successfully added',
                    type: 'success',
                  });
                  if (result._id) {
                    props.navigation.navigate('ProfileNavigator', {
                      screen: 'ProfileScreen',
                      params: {
                        titleScreen: result.name,
                        name: result.name,
                        id: result._id,
                      },
                    });
                  } else {
                    props.navigation.navigate('UsersScreen');
                  }
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  (error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                  'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Title title={isEdit ? 'Edit User' : 'Add User'}>
                    <Button
                      rounded={true}
                      buttonStyle={{ width: 120 }}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          {isEdit ? 'UPDATE' : 'SAVE'}
                        </SociollaText>
                      }
                      onPress={handleSubmit}
                    />
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper
                rounded
                style={isNative ? { paddingBottom: isAndroid ? 140 : 60 } : {}}
                isHeight={values.role ? false : true}>
                <MobileView>
                  <Layout.FormWrapper
                    style={{
                      paddingHorizontal: 15,
                      ...(isWeb
                        ? {
                            paddingHorizontal: isWeb ? 20 : 30,
                            paddingVertical: isWeb ? 20 : 15,
                          }
                        : {}),
                    }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        marginBottom: 15,
                        width: '100%',
                        marginTop: isWeb ? 0 : 20,
                      }}>
                      <Photo
                        width={photoSize}
                        height={photoSize}
                        touchable={true}
                        onPress={async () => {
                          const uploadImage = await chooseFile();
                          if (uploadImage) {
                            setFieldValue('image', uploadImage);
                          }
                        }}
                        image={values.image}
                      />
                    </View>
                    <View
                      style={{
                        width: '100%',
                        flexDirection: isWeb ? 'row' : 'column',
                        ...(isWeb
                          ? {}
                          : {
                              height: '100%',
                            }),
                      }}>
                      <View style={{ width: !isWeb ? '100%' : '48%' }}>
                        <Layout.FormGroup>
                          <Input
                            label="Name"
                            value={values.name}
                            isRequired={true}
                            placeholder="Name"
                            hasError={
                              errors.name && touched.name ? errors.name : null
                            }
                            onChangeText={handleChange('name')}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Email"
                            value={values.email}
                            isRequired={true}
                            placeholder="Email"
                            hasError={
                              errors.email && touched.email
                                ? errors.email
                                : null
                            }
                            onChangeText={handleChange('email')}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Phone Number"
                            value={values.phone}
                            placeholder="Phone Number"
                            keyboardType="numeric"
                            maxLength={13}
                            isRequired={true}
                            isNumeric={true}
                            hasError={
                              errors.phone && touched.phone
                                ? errors.phone
                                : null
                            }
                            onChangeText={(val) => setFieldValue('phone', val)}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            label="Country"
                            options={countryList}
                            value={values.country}
                            placeholder="Select Country"
                            trackBy="value"
                            labelKey="name"
                            hasError={
                              errors.country && touched.country
                                ? errors.country
                                : null
                            }
                            isRequired={true}
                            editable={true}
                            multiple={true}
                            hideSearch={true}
                            onChange={(value) =>
                              setFieldValue('country', value)
                            }
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            label="Role"
                            options={roleLists}
                            value={values.role}
                            placeholder="Select Role"
                            trackBy="id"
                            labelKey="name"
                            isRequired={true}
                            // editable={isEdit ? !requiredField(userData.role, ['superadmin','sales_admin']) : true}
                            hasError={
                              errors.role && touched.role ? errors.role : null
                            }
                            onChange={(value) => {
                              setIsLoading({ ...isLoading, branch: true });
                              setTimeout(() => {
                                setIsLoading({ ...isLoading, branch: false });
                              }, 500);
                              setFieldValue('branch', []);
                              setFieldValue('sales_team', []);
                              setFieldValue('sales_code', '');
                              setFieldValue('salesman_type', '');
                              setFieldValue('role', value);
                            }}
                          />
                        </Layout.FormGroup>
                      </View>
                      {values.role && values.role.code ? (
                        <View style={{ width: !isWeb ? '100%' : '48%' }}>
                          <Layout.FormGroup>
                            <BranchSearch
                              customFilter={{ is_active: true }}
                              value={values.branch}
                              hasError={
                                errors.branch && touched.branch
                                  ? errors.branch
                                  : null
                              }
                              isRequired={requiredField(values.role, [
                                'vice_president',
                                'distributor_manager',
                                'general_manager',
                                'superadmin',
                                'general',
                                ...disabled_some_fields,
                              ])}
                              editable={requiredField(values.role, [
                                'vice_president',
                                'distributor_manager',
                                'general_manager',
                                'superadmin',
                                'general',
                                ...disabled_some_fields,
                              ])}
                              // multiple={requiredField(values.role, ['branch_head'])}
                              multiple={true}
                              loading={
                                isLoading.branch ||
                                (isEdit && values.branch?.length
                                  ? getBranches.loading
                                  : false)
                              }
                              arrayOne={requiredField(values.role, [
                                'branch_head',
                                'regional_manager',
                                'regional_support',
                                'key_account_manager',
                                'group_key_account_manager',
                                'national_key_account_manager',
                                'sales_admin',
                                'beauty_advisor',
                                'merchandiser',
                                'collector',
                                'spv_logistic',
                                'regional_distributor_manager',
                                'sales_supervisor',
                              ])}
                              // customFilter={
                              //   values?.role?.code == 'branch_head'
                              //     ? values?.branch?.length
                              //       ? {
                              //           $or: [
                              //             {
                              //               _id: {
                              //                 $in: values.branch.map(
                              //                   (a) => a.id,
                              //                 ),
                              //               },
                              //             },
                              //           ],
                              //         }
                              //       : {}
                              //     : {}
                              // }
                              onChange={(value) => {
                                let branchSelected = [];
                                if (!value?.length) {
                                  setFieldValue('sales_team', []);
                                  setSalesTeamTemp([]);
                                }
                                if (
                                  !requiredField(values.role, [
                                    'branch_head',
                                    'regional_manager',
                                    'regional_support',
                                    'key_account_manager',
                                    'group_key_account_manager',
                                    'national_key_account_manager',
                                    'sales_admin',
                                    'beauty_advisor',
                                    'merchandiser',
                                    'collector',
                                    'spv_logistic',
                                    'regional_distributor_manager',
                                    'sales_supervisor',
                                  ])
                                ) {
                                  if (value?.length) {
                                    branchSelected = value;
                                  }
                                  setFieldValue('branch', value);
                                } else {
                                  if (value?.length) {
                                    branchSelected = [value[value.length - 1]];
                                  }
                                  setFieldValue('branch', branchSelected);
                                }
                                if (
                                  !requiredField(values.role, [
                                    'branch_head',
                                    'finance_supervisor',
                                    'regional_manager',
                                    'regional_support',
                                    'key_account_manager',
                                    'group_key_account_manager',
                                    'national_key_account_manager',
                                    'salesman',
                                    'sales_supervisor',
                                    'sales_admin',
                                    'beauty_advisor',
                                    'merchandiser',
                                    'collector',
                                    'spv_logistic',
                                    'regional_distributor_manager',
                                    'sales_supervisor',
                                  ])
                                ) {
                                  if (branchSelected?.length) {
                                    const teams = [];
                                    for (
                                      let j = 0;
                                      j < branchSelected.length;
                                      j++
                                    ) {
                                      const child = branchSelected[j];
                                      if (
                                        child &&
                                        child.sales_team &&
                                        child.sales_team.length
                                      ) {
                                        for (
                                          let k = 0;
                                          k < child.sales_team.length;
                                          k++
                                        ) {
                                          const tm = child.sales_team[k];
                                          if (
                                            tm &&
                                            tm.id &&
                                            teams.findIndex(
                                              (a) => a.id == tm.id,
                                            ) == -1
                                          ) {
                                            teams.push(tm);
                                          }
                                        }
                                      }
                                    }
                                    if (
                                      !requiredField(values.role, [
                                        'branch_head',
                                        'regional_manager',
                                        'regional_support',
                                        'key_account_manager',
                                        'group_key_account_manager',
                                        'national_key_account_manager',
                                        'sales_admin',
                                        'beauty_advisor',
                                        'merchandiser',
                                        'collector',
                                        'spv_logistic',
                                        'regional_distributor_manager',
                                        'sales_supervisor',
                                      ])
                                    ) {
                                      setFieldValue('sales_team', teams);
                                    } else if (
                                      !requiredField(values.role, [
                                        'salesman',
                                        'sales_supervisor',
                                        'finance_supervisor',
                                      ])
                                    ) {
                                      setSalesTeamTemp(teams);
                                      setFieldValue('sales_team', []);
                                    } else {
                                      setSalesTeamTemp([]);
                                      setFieldValue('sales_team', []);
                                    }
                                  } else {
                                    setSalesTeamTemp([]);
                                  }
                                } else if (!branchSelected?.length) {
                                  setSalesTeamTemp([]);
                                }
                              }}
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup>
                            <SelectBox
                              label="Sales Team"
                              options={
                                !requiredField(values.role, [
                                  'salesman',
                                  'sales_supervisor',
                                  'finance_supervisor',
                                  'sales_admin',
                                ])
                                  ? salesTeamTemp
                                  : []
                              }
                              value={values.sales_team}
                              placeholder="Select Sales Team"
                              trackBy="id"
                              labelKey="name"
                              hasError={
                                errors.sales_team && touched.sales_team
                                  ? errors.sales_team
                                  : null
                              }
                              isRequired={requiredField(values.role, [
                                'vice_president',
                                'distributor_manager',
                                'general_manager',
                                'superadmin',
                                'general',
                                ...disabled_some_fields,
                              ])}
                              editable={requiredField(values.role, [
                                'vice_president',
                                'distributor_manager',
                                'general_manager',
                                'superadmin',
                                'branch_head',
                                'regional_manager',
                                'regional_support',
                                'key_account_manager',
                                'group_key_account_manager',
                                'general',
                                'regional_distributor_manager',
                                ...disabled_some_fields,
                              ])}
                              multiple={true}
                              onChange={(value) =>
                                setFieldValue('sales_team', value)
                              }
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup>
                            <Input
                              label="Sales Code"
                              value={values.sales_code}
                              hasError={
                                errors.sales_code && touched.sales_code
                                  ? errors.sales_code
                                  : null
                              }
                              isRequired={
                                !requiredField(values.role, sales_code_roles)
                              }
                              editable={
                                !requiredField(values.role, sales_code_roles)
                              }
                              onChangeText={handleChange('sales_code')}
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup>
                            <SelectBox
                              label="Salesman Type"
                              options={SALESMAN_TYPE}
                              value={
                                values.salesman_type
                                  ? SALESMAN_TYPE.find(
                                      (sm) => sm.value == values.salesman_type,
                                    )
                                  : ''
                              }
                              placeholder="Select Salesman Type"
                              trackBy="value"
                              labelKey="label"
                              hasError={
                                errors.salesman_type && touched.salesman_type
                                  ? errors.salesman_type
                                  : null
                              }
                              isRequired={
                                !requiredField(values.role, salesman_type_roles)
                              }
                              editable={
                                !requiredField(values.role, salesman_type_roles)
                              }
                              onChange={(value) =>
                                setFieldValue('salesman_type', value.value)
                              }
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup>
                            <SelectBox
                              label="Carrier"
                              options={carriers}
                              value={values.carrier}
                              placeholder="Select Carrier"
                              trackBy="id"
                              labelKey="name"
                              loading={carriersLoading}
                              hasError={
                                errors.carrier && touched.carrier
                                  ? errors.carrier
                                  : null
                              }
                              hideSearch={true}
                              withCallback={true}
                              isRequired={!requiredField(values.role, ['3pl'])}
                              editable={!requiredField(values.role, ['3pl'])}
                              onChange={(value) => {
                                setFieldValue('carrier', value);
                              }}
                              onLoadMore={(keyword) => {
                                getCarriers({});
                              }}
                              searchCallback={(keyword) => {
                                getCarriers({
                                  val: keyword,
                                });
                              }}
                              onReady={() => {
                                getCarriers({});
                              }}
                            />
                          </Layout.FormGroup>
                        </View>
                      ) : (
                        false
                      )}
                    </View>
                  </Layout.FormWrapper>
                </MobileView>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  <Button
                    rounded={true}
                    buttonStyle={{ width: '100%' }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        {isEdit ? 'UPDATE' : 'SAVE'}
                      </SociollaText>
                    }
                    onPress={handleSubmit}
                  />
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      )}
    </Layout.Container>
  );
};

export default UserFormScreen;
